import React, { PropTypes } from 'react';
import { Form, FormGroup, FormControl, Col, Checkbox, Radio, InputGroup } from 'react-bootstrap';
import Datetime from 'react-datetime';
import Switch from 'react-bootstrap-switch';
import moment from 'moment';
import _ from 'lodash';

import ArrayUtils from 'PMT/utils/ArrayUtils';
import AvailabilityHours from 'PMT/entities/menu/AvailabilityHours';
import I18n from 'PMT/i18n/I18n';
import Order from 'PMT/entities/order/Order';
import CartModifier from 'PMT/entities/menu/CartModifier';
import OrderViewUtils from 'PMT/utils/OrderViewUtils';
import ObjectUtils from 'PMT/utils/ObjectUtils';

import AbstractComponent from '../../../../components/AbstractComponent';
import AvailabilityHoursForm from '../../../menu/components/availabilityHours/AvailabilityHoursForm';
import ConditionRules from '../components/ConditionRules';
import TipTooltip from 'PMT/components/TipTooltip';
import { TextInput, TextInputLocalized } from 'PMT/components/form';

import Constants from 'constants/Constants';
import {CurrencyUtils} from 'PMT/utils';

export default class View extends AbstractComponent {
  static propTypes = {
    restaurantMenu: PropTypes.object.isRequired,
    cartModifier: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    currencyCode: PropTypes.string,
  };

  state = {
    switchPeriod: false,
    switchWeeklyAvailabilities: false,
    switchApiConsumerIds: false,
    switchComposition: false,
    switchShippingCost: false,
  };

  componentDidMount() {
    this.updateStateWithNextProps(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.updateStateWithNextProps(nextProps);
  }

  updateStateWithNextProps = nextProps => {
    const { cartModifier } = nextProps;

    this.setState({
      switchPeriod: cartModifier.hasDefinedDateCondition(),
      switchWeeklyAvailabilities: cartModifier.hasDefinedWeeklyAvailabilities(),
      switchComposition: cartModifier.hasDefinedComposition(),
      switchApiConsumerIds: cartModifier.hasDefinedApiConsumerIds(),
    });
  };

  handleChange = (path, value) => {
    this.props.onChange(ObjectUtils.setForPath(this.props.cartModifier.copy(), path, value));
  };

  onSwitch = key => {
    switch (key) {
      case 'switchPeriod':
        if (this.state[key]) {
          this.handleChange('condition.dateCondition.beginningDate', null);
          this.handleChange('condition.dateCondition.endDate', null);
        } else {
          this.handleChange('condition.dateCondition.beginningDate', moment());
          this.handleChange('condition.dateCondition.endDate', moment());
        }
        break;

      case 'switchWeeklyAvailabilities':
        if (this.state[key]) {
          this.handleChange('condition.dateCondition.weeklyAvailabilities', null);
        } else {
          this.handleChange(
            'condition.dateCondition.weeklyAvailabilities',
            new AvailabilityHours()
          );
        }
        break;

      case 'switchComposition':
        if (this.state[key]) {
          this.handleChange('condition.itemCondition.itemsRequired', null);
          this.handleChange('condition.itemCondition.itemsForbidden', null);
        } else {
          this.handleChange('condition.itemCondition.itemsRequired', []);
          this.handleChange('condition.itemCondition.itemsForbidden', []);
        }
        break;

      case 'switchApiConsumerIds':
        if (this.state[key]) {
          this.handleChange('condition.frontCondition.apiConsumerIds', null);
        } else {
          this.handleChange('condition.frontCondition.apiConsumerIds', []);
        }
        break;

      default:
        let newState = {};
        newState[key] = !this.state[key];
        this.setState(newState);
    }
  };

  render() {
    const apiConsumers = this.props.apiConsumers;
    const cartModifier = this.props.cartModifier;
    const currencyCode = this.props.currencyCode;
    const {
      localizedNames,
      localizedDescriptions,
      localizedTags,
      combinable,
      code,
      condition,
      target,
      action,
    } = cartModifier;

    return (
      <Form horizontal>
        <div>
          <h4 className="page-header">{I18n.tr('order.cart_modifiers.general.title')}</h4>

          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.general.name.label')}&nbsp;*
            </Col>
            <Col sm={6} md={5}>
              <TextInputLocalized
                locales={Constants.localesForTranslation}
                values={localizedNames}
                isRequired
                className="form-control"
                placeholder={I18n.tr('order.cart_modifiers.general.name.placeholder')}
                onChange={value => this.handleChange('localizedNames', value)}
              />
            </Col>
          </FormGroup>

          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.general.tag.label')}&nbsp;*
            </Col>
            <Col sm={6} md={5}>
              <TextInputLocalized
                locales={Constants.localesForTranslation}
                values={localizedTags}
                className="form-control"
                placeholder={I18n.tr('order.cart_modifiers.general.tag.placeholder')}
                max={CartModifier.TAG_LIMIT}
                onChange={value => this.handleChange('localizedTags', value)}
              />
            </Col>
          </FormGroup>

          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.general.description.label')}
            </Col>
            <Col sm={6} md={5}>
              <TextInputLocalized
                locales={Constants.localesForTranslation}
                values={localizedDescriptions}
                className="form-control"
                placeholder={I18n.tr('order.cart_modifiers.general.description.placeholder')}
                onChange={value => this.handleChange('localizedDescriptions', value)}
              />
            </Col>
          </FormGroup>

          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.general.combinable.label')}
            </Col>
            <Col sm={6} md={5}>
              <Switch
                bsSize="mini"
                onChange={() => {
                  this.handleChange('combinable', !combinable);
                }}
                value={combinable}
              />
            </Col>
          </FormGroup>

          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.general.code.label')}
              <TipTooltip text={I18n.tr('order.cart_modifiers.general.code.tooltip')} />
            </Col>
            <Col sm={6} md={5}>
              <TextInput
                value={code}
                className="form-control"
                onChange={value => this.handleChange('code', _.isEmpty(value) ? null : value)}
              />
            </Col>
          </FormGroup>
        </div>
        <div>
          <h4 className="page-header">{I18n.tr('order.cart_modifiers.conditions_rules.title')}</h4>
          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.conditions_rules.dateCondition.title')}
            </Col>
            <Col sm={6} md={5}>
              <Switch
                bsSize="mini"
                onChange={() => this.onSwitch('switchPeriod')}
                value={this.state.switchPeriod}
              />
            </Col>
          </FormGroup>
          {this.state.switchPeriod && (
            <FormGroup>
              <Col sm={6} smOffset={3} md={4}>
                <InputGroup>
                  <InputGroup.Addon>
                    {I18n.tr('order.cart_modifiers.conditions_rules.dateCondition.from')}
                  </InputGroup.Addon>
                  <Datetime
                    value={moment(condition.dateCondition.beginningDate)}
                    onChange={date =>
                      this.handleChange('condition.dateCondition.beginningDate', date)
                    }
                  />
                </InputGroup>
              </Col>
              <Col sm={6} smOffset={3} md={4} mdOffset={0}>
                <InputGroup>
                  <InputGroup.Addon>
                    {I18n.tr('order.cart_modifiers.conditions_rules.dateCondition.to')}
                  </InputGroup.Addon>
                  <Datetime
                    value={moment(condition.dateCondition.endDate)}
                    onChange={date => this.handleChange('condition.dateCondition.endDate', date)}
                  />
                </InputGroup>
              </Col>
            </FormGroup>
          )}
          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.conditions_rules.weekly_availabilities.title')}
            </Col>
            <Col sm={6} md={5}>
              <Switch
                bsSize="mini"
                onChange={() => this.onSwitch('switchWeeklyAvailabilities')}
                value={this.state.switchWeeklyAvailabilities}
              />
            </Col>
          </FormGroup>
          {this.state.switchWeeklyAvailabilities && (
            <FormGroup>
              <Col smOffset={3} xs={9}>
                <AvailabilityHoursForm
                  availabilityHours={condition.dateCondition.weeklyAvailabilities.copy()}
                  onAvailibilityHoursChange={availabilities =>
                    this.handleChange(
                      'condition.dateCondition.weeklyAvailabilities',
                      availabilities
                    )
                  }
                />
              </Col>
            </FormGroup>
          )}
          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.conditions_rules.mode.title')}
            </Col>
            <Col sm={6} md={5}>
              {_.values(Order.OrderMode).map(mode => {
                return (
                  <Checkbox
                    key={mode}
                    checked={cartModifier.isAvailableForMode(mode)}
                    value={mode}
                    onChange={e => {
                      this.handleChange(
                        'condition.orderModes',
                        _.isNull(condition.orderModes)
                          ? _.values(Order.OrderMode).filter(v => {
                              return v !== parseInt(e.target.value);
                            })
                          : condition.orderModes.includes(parseInt(e.target.value))
                          ? condition.orderModes.filter(v => {
                              return v !== parseInt(e.target.value);
                            })
                          : [...condition.orderModes, parseInt(e.target.value)]
                      );
                    }}
                    inline
                  >
                    {OrderViewUtils.getLabelForMode(mode)}
                  </Checkbox>
                );
              })}
            </Col>
          </FormGroup>
          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.conditions_rules.min_amount.title')}
            </Col>
            <Col sm={2}>
              <InputGroup>
                <FormControl
                  type="number"
                  value={condition.amountCondition.minOrderAmount}
                  onChange={e => {
                    this.handleChange('condition.amountCondition.minOrderAmount', e.target.value);
                  }}
                />
                <InputGroup.Addon>
                  {CurrencyUtils.getCurrencySymbol(currencyCode)}
                </InputGroup.Addon>
              </InputGroup>
            </Col>
            <Col sm={4}>
              <FormControl
                componentClass="select"
                placeholder="select"
                value={condition.amountCondition.includeShippingCostInMinOrderAmount}
                onChange={e => {
                  this.handleChange(
                    'condition.amountCondition.includeShippingCostInMinOrderAmount',
                    e.target.value
                  );
                }}
              >
                <option value={false}>
                  {I18n.tr(`order.cart_modifiers.conditions_rules.amount.shipingCostExcluded`)}
                </option>
                <option value={true}>
                  {I18n.tr(`order.cart_modifiers.conditions_rules.amount.shipingCostIncluded`)}
                </option>
              </FormControl>
            </Col>
          </FormGroup>
          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.conditions_rules.max_amount.title')}
            </Col>
            <Col sm={2}>
              <InputGroup>
                <FormControl
                  type="number"
                  value={condition.amountCondition.maxOrderAmount}
                  onChange={e => {
                    this.handleChange('condition.amountCondition.maxOrderAmount', e.target.value);
                  }}
                />
                <InputGroup.Addon>
                  {CurrencyUtils.getCurrencySymbol(currencyCode)}
                </InputGroup.Addon>
              </InputGroup>
            </Col>
            <Col sm={4}>
              <FormControl
                componentClass="select"
                placeholder="select"
                value={condition.amountCondition.includeShippingCostInMaxOrderAmount}
                onChange={e => {
                  this.handleChange(
                    'condition.amountCondition.includeShippingCostInMaxOrderAmount',
                    e.target.value
                  );
                }}
              >
                <option value={false}>
                  {I18n.tr(`order.cart_modifiers.conditions_rules.amount.shipingCostExcluded`)}
                </option>
                <option value={true}>
                  {I18n.tr(`order.cart_modifiers.conditions_rules.amount.shipingCostIncluded`)}
                </option>
              </FormControl>
            </Col>
          </FormGroup>
          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.conditions_rules.composition.title')}
            </Col>
            <Col sm={6} md={5}>
              <Switch
                bsSize="mini"
                onChange={() => this.onSwitch('switchComposition')}
                value={this.state.switchComposition}
              />
            </Col>
          </FormGroup>
          {this.state.switchComposition && (
            <FormGroup>
              <Col smOffset={3} lg={9}>
                <ConditionRules
                  restaurantMenu={this.props.restaurantMenu}
                  conditionRules={condition.itemCondition.itemsRequired}
                  onChange={conditionRules =>
                    this.handleChange('condition.itemCondition.itemsRequired', conditionRules)
                  }
                />
              </Col>
            </FormGroup>
          )}
          <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.conditions_rules.front.apiConsumerIds.title')}
            </Col>
            <Col sm={6} md={5}>
              <Switch
                bsSize="mini"
                onChange={() => this.onSwitch('switchApiConsumerIds')}
                value={this.state.switchApiConsumerIds}
              />
            </Col>
          </FormGroup>
          {this.state.switchApiConsumerIds && apiConsumers && (
            <FormGroup>
              <Col smOffset={3} lg={9}>
                {apiConsumers.map(({ id, name }) => (
                  <div key={id}>
                    <Checkbox
                      checked={(condition.frontCondition.apiConsumerIds || []).includes(id)}
                      value={id}
                      onChange={e => {
                        let newApiConsumerIds = [...condition.frontCondition.apiConsumerIds];
                        newApiConsumerIds = ArrayUtils.toggleValue(newApiConsumerIds, id);
                        this.handleChange(
                          'condition.frontCondition.apiConsumerIds',
                          newApiConsumerIds
                        );
                      }}
                      inline
                    >
                      {name}
                    </Checkbox>
                  </div>
                ))}
              </Col>
            </FormGroup>
          )}
        </div>
        <div>
          <h4 className="page-header">{I18n.tr('order.cart_modifiers.actions.title')}</h4>

          {/* SHIPPING_COST */}
          {/* DISABLED FOR V1
            <FormGroup>
            <Col componentClass="label" className="control-label" sm={3}>
              {I18n.tr('order.cart_modifiers.actions.offering_shipping_cost.title')}
            </Col>
            <Col sm={6} md={5}>
              <Switch
            bsSize="mini"
            onChange={({ state }) => {
            this.handleChange('target.type', state.value ? CartModifier.TargetType.SHIPPING_COST : CartModifier.TargetType.SPECIFIC_ITEMS)
            }}
            value={cartModifier.isOfferingShippingCost()}
              />
            </Col>
          </FormGroup> */}

          {!cartModifier.isOfferingShippingCost() && (
            <div>
              <FormGroup>
                <Col componentClass="label" className="control-label" sm={3}>
                  {I18n.tr('order.cart_modifiers.actions.offering_shipping_cost.reduction')}
                </Col>
                <Col sm={5}>
                  {/*  DISABLED FOR V1
                    <Radio
                    name="discountType"
                    value=""
                    checked={!cartModifier.hasDefinedActionType()}
                    onChange={(e) => {
                      this.handleChange('action.type', null)
                    }}
                    >
                    {I18n.tr('order.cart_modifiers.actions.offering_shipping_cost.none')}
                  </Radio> */}

                  {_.keys(CartModifier.ActionType).map(type => (
                    <Radio
                      key={type}
                      name="discountType"
                      value={type}
                      checked={cartModifier.hasActionType(type)}
                      onChange={e => {
                        this.handleChange('action.type', type);
                      }}
                    >
                      {I18n.tr(`order.cart_modifiers.actiontype.${type}`)}
                    </Radio>
                  ))}
                </Col>

                {cartModifier.hasDefinedActionType() && (
                  <Col sm={3}>
                    <InputGroup>
                      <FormControl
                        type="number"
                        value={action.value}
                        onChange={e => {
                          this.handleChange('action.value', e.target.value);
                        }}
                      />
                      <InputGroup.Addon>
                        {action.type === CartModifier.ActionType.DISCOUNT_PERCENTAGE
                          ? '%'
                          : action.type === CartModifier.ActionType.DISCOUNT_AMOUNT
                                ?
                                CurrencyUtils.getCurrencySymbol(currencyCode)
                          : ''}
                      </InputGroup.Addon>
                    </InputGroup>
                  </Col>
                )}
              </FormGroup>

              {cartModifier.hasDefinedActionType() && (
                <div>
                  <FormGroup>
                    <Col componentClass="label" className="control-label" sm={3}>
                      {I18n.tr('order.cart_modifiers.target')}
                    </Col>
                    <Col sm={5}>
                      <FormControl
                        componentClass="select"
                        placeholder="select"
                        value={target.type}
                        onChange={e => {
                          this.handleChange('target.type', e.target.value);
                        }}
                      >
                        <option
                          value={false}
                          disabled
                          selected={!cartModifier.hasDefinedTargetType()}
                        >
                          {I18n.tr('order.cart_modifiers.target.type.title')}
                        </option>
                        {_.keys(CartModifier.TargetType).map(type => (
                          <option key={type} value={type}>
                            {I18n.tr(`order.cart_modifiers.target_type.${type}`)}
                          </option>
                        ))}
                      </FormControl>
                    </Col>
                  </FormGroup>

                  {(target.type === CartModifier.TargetType.SPECIFIC_ITEMS ||
                    target.type === CartModifier.TargetType.ALL_ITEMS_EXCEPT) && (
                    <div>
                      <FormGroup>
                        <Col componentClass="label" className="control-label" sm={3}>
                          {I18n.tr('order.cart_modifiers.cheapest_item')}
                        </Col>
                        <Col sm={6}>
                          <Switch
                            bsSize="mini"
                            onChange={({ state }) => {
                              this.handleChange('target.focusOnCheapestItems', state.value);
                            }}
                            value={cartModifier.isFocusOnCheapestItems()}
                          />
                        </Col>
                      </FormGroup>
                      <FormGroup>
                        <Col smOffset={3} lg={9}>
                          <ConditionRules
                            restaurantMenu={this.props.restaurantMenu}
                            singleMode
                            // the conditionRules requires an array but here we want just an object
                            conditionRules={_.isNull(target.items) ? [] : [target.items]}
                            onChange={conditionRules =>
                              this.handleChange('target.items', conditionRules[0])
                            }
                          />
                        </Col>
                      </FormGroup>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/pages/order/cartModifiers/edit/View.js